import './HeatingSettings.css';
import NumericInput from './NumericInput.js';
import React, { useState } from 'react';
import { t, getSettingLabel, translateBackendValue, translateDayName } from './i18n.js';

// Helper function to get min value for numeric settings
const getSettingMin = (key) => {
  if (key === 'calibration_value' || key === 'adjustment_value') return -15;
  if (key === 'override_minutes') return 1;
  return 0;
};

// Helper function to get max value for numeric settings
const getSettingMax = (key) => {
  if (key === 'calibration_value' || key === 'adjustment_value') return 15;
  if (key === 'override_minutes') return 180;
  return 60;
};

// Helper function to get step value for numeric settings
const getSettingStep = (key) => {
  if (key === 'calibration_value' || key === 'adjustment_value') return 0.5;
  return 1;
};


export function RoomTable({ roomData }) {
  return (
    <div>
      <p></p>
      <h3>{t('room.title')}</h3>
      <table className="room_table">
        <thead>
          <tr>
            <th>{t('room.name')}</th>
            <th>{t('room.profile')}</th>
            <th>{t('room.thermometer')}</th>
            <th>{t('room.heaters')}</th>
          </tr>
        </thead>
        <tbody>
          {roomData.map((room, index) => (
            <tr key={index}>
              <td>{room.name}</td>
              <td>{room.profile}</td>
              <td>{room.thermometer}</td>
              <td>
                {room.heaters.map((heater, idx) => (
                  <div key={idx}>{heater}</div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BasicInfo({ basicInfo, onSchemeChange, onSummerModeChange }) {
  const summerMode = basicInfo.summer_mode || { active: false, decrease: -2 };

  return (
    <div>
      <h3>{t('basic.title')}</h3>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>
          <input
            type="checkbox"
            checked={!!summerMode.active}
            onChange={(e) => onSummerModeChange({ ...summerMode, active: e.target.checked })}
            style={{ marginRight: '0.4rem' }}
          />
          {t('basic.summerMode')}
        </label>
        {summerMode.active && (
          <span style={{ marginLeft: '1rem' }}>
            {t('basic.summerDecrease')}:{' '}
            <NumericInput
              value={summerMode.decrease}
              onChange={(value) => onSummerModeChange({ ...summerMode, decrease: value })}
              min={-10}
              max={0}
              step={0.5}
            />
            {' '}°C
          </span>
        )}
      </div>

      <h4>{t('basic.chooseScheme')}</h4>
      {Object.entries(basicInfo.schemes)
        .filter(([key]) => key !== "active" && key !== "time_based_adjustments")
        .map(([key, value]) => (
          <span key={key} className="radio-option">
            <label>
              <input
                type="radio"
                value={key}
                checked={basicInfo.schemes.active === key}
                onChange={() => onSchemeChange(key)}
              />
              {value}
            </label>
          </span>
        ))}
    </div>
  );
}

export function TimeBasedAdjustments({ basicInfo, onUpdateAdjustment }) {
  const validateAdjustment = (adjustment) => {
    const { value, start_at, end_at, active_days } = adjustment;

    // Check if value is non-zero
    if (value === 0) {
      return { isValid: false, error: t('timeAdj.errorValueZero') };
    }

    // Check if start_at and end_at are valid
    if (start_at === undefined || end_at === undefined) {
      return { isValid: false, error: t('timeAdj.errorMissingTime') };
    }
    if (start_at < 0 || start_at > 23 || end_at < 0 || end_at > 23) {
      return { isValid: false, error: t('timeAdj.errorInvalidTime') };
    }

    // Check if active_days is a valid object
    if (!active_days || typeof active_days !== "object") {
      return { isValid: false, error: t('timeAdj.errorMissingDays') };
    }

    // Check if at least one day is active
    const hasActiveDay = Object.values(active_days).some((isActive) => isActive);
    if (!hasActiveDay) {
      return { isValid: false, error: t('timeAdj.errorNoActiveDay') };
    }

    // All validations passed
    return { isValid: true, error: null };
  };

  const handleFieldChange = (id, field, value) => {
    const adjustment = basicInfo.time_based_adjustments[id];
    const updatedAdjustment = {
      ...adjustment,
      [field]: value,
    };
    onUpdateAdjustment(id, updatedAdjustment); // Notify the parent component
  };

  const handleDayToggle = (id, day) => {
    const adjustment = basicInfo.time_based_adjustments[id];
    const updatedActiveDays = {
      ...adjustment.active_days,
      [day]: !adjustment.active_days[day], // Toggle the active state of the day
    };
    const updatedAdjustment = {
      ...adjustment,
      active_days: updatedActiveDays,
    };
    onUpdateAdjustment(id, updatedAdjustment); // Notify the parent component
  };

  const timeBasedAdjustments = basicInfo?.time_based_adjustments || {};

  return (
    <div>
      {timeBasedAdjustments && Object.keys(timeBasedAdjustments).length !== 0 && (
        <div className="adjustments-table-container">
          <h4>{t('timeAdj.title')}</h4>
          <table className="profile-table">
            <thead>
              <tr>
                <th>{t('timeAdj.name')}</th>
                <th>{t('timeAdj.degrees')}</th>
                <th>{t('timeAdj.time')}</th>
                <th>{t('timeAdj.activeDays')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(timeBasedAdjustments).map(([id, adjustment]) => {
                const { isValid, error } = validateAdjustment(adjustment);
                const activeDays = adjustment.active_days || {};

                return (
                  <tr key={id} className={isValid ? "" : "disabled"}>
                    <td>{translateBackendValue(adjustment.name)}</td>
                    <td>
                      <NumericInput
                        value={adjustment.value}
                        onChange={(value) => handleFieldChange(id, "value", value)}
                        min={-5}
                        max={-1}
                        step={1}
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <NumericInput
                          value={adjustment.start_at}
                          onChange={(value) => handleFieldChange(id, "start_at", value)}
                          min={0}
                          max={23}
                          step={1}
                          style={{ width: '40px' }}
                        />
                        <span style={{ margin: '5px' }}>-</span>
                        <NumericInput
                          value={adjustment.end_at}
                          onChange={(value) => handleFieldChange(id, "end_at", value)}
                          min={0}
                          max={23}
                          step={1}
                          style={{ width: '40px' }}
                        />
                      </div>
                    </td>

                    <td>
                      {Object.entries(activeDays).map(([day, isActive]) => {
                        const translatedDay = translateDayName(day);
                        // Finnish uses 2 letters (Ma, Ti), English uses 3 (Mon, Tue)
                        const dayAbbr = translatedDay.substring(0, translatedDay.length >= 5 ? 2 : 3);
                        return (
                          <label key={day} style={{ marginRight: "10px" }}>
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() => handleDayToggle(id, day)}
                            />
                            {dayAbbr}
                          </label>
                        );
                      })}
                    </td>
                    <td>{isValid ? "" : `${t('timeAdj.warning')}: ${error}`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ProfileTable({ profileConfigs, basicInfo, onTimeBasedAdjustmentToggle, onToggleEnabled, onToggleOptimisationEnabled, onReserveChange, onReserveHeatingAfternoonChange, onPriceChange, onPriceDropChange, onThermostatSettingChange }) {
  const summerMode = basicInfo?.summer_mode || {};
  const summerActive = !!summerMode.active;
  const isIphone = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div>
      {isIphone && (
        <div>
          <i>{t('profile.mobileWarning')}</i>
        </div>
      )}
      <div className="profile-tables-container">
        <p></p>
        <h3>{t('profile.title')}</h3>
        {profileConfigs.map((profile) => (
          <div key={profile.id} className="profile-table">
            <h4>{profile.name}</h4>
            <label>
              {t('common.enabled')}:
              <input
                type="checkbox"
                checked={profile.enabled}
                onChange={(e) => onToggleEnabled(profile.id, 'enabled', e.target.checked)}
                //disabled={true}
              />
            </label>
            {profile.optimisation_enabled !== undefined ? (
              <div>
                <label>
                  {t('profile.optimisationEnabled')}:
                  <input
                    type="checkbox"
                    checked={profile.optimisation_enabled}
                    onChange={(e) => onToggleOptimisationEnabled(profile.id, 'optimisation_enabled', e.target.checked)}
                    //disabled={true}
                  />
                </label>
              </div>
            ) : null}
            {summerActive && (
              <p style={{ fontStyle: 'italic', color: '#b45309', margin: '0.4rem 0 0.2rem' }}>
                {t('profile.summerModeNote', { decrease: summerMode.decrease })}
              </p>
            )}
            {summerActive && (() => {
              const coolingNames = Object.values(profile.thermostat_settings || {})
                .filter(s => s.cooling_mode?.active)
                .map(s => s.name)
                .filter(Boolean);
              return coolingNames.length > 0 ? (
                <p style={{ fontStyle: 'italic', color: '#0369a1', margin: '0 0 0.2rem' }}>
                  {t('profile.coolingModeNote', { names: coolingNames.join(', ') })}
                </p>
              ) : null;
            })()}
            <table className="profile-table">
              <thead>
                <tr>
                  <th>{t('profile.priceRanges')}</th>
                  {profile.price_ranges[0] && Object.keys(profile.price_ranges[0])
                    .filter(key => key !== 'id' && key !== 'range')
                    .map(scheme => (
                      <th key={scheme}>{t('profile.targetTempScheme')} {scheme.toUpperCase()} {t('profile.degreesUnit')}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {profile.price_ranges.map((range, index) => (
                  <tr key={range.id}>
                    <td>{range.range}</td>
                    {Object.keys(range)
                      .filter(key => key !== 'id' && key !== 'range')
                      .map(scheme => (
                        <td key={scheme}>
                          <NumericInput
                            value={range[scheme]}
                            onChange={(value) => onPriceChange(profile.id, range.id, scheme, value)}
                            min={7}
                            max={40}
                            step={0.5}
                          />
                        </td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {profile.time_based_adjustments !== undefined ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>{t('profile.timeBasedAdj')}</th>
                    {Object.keys(basicInfo.schemes ? basicInfo.schemes : {}).map((scheme, index) => (
                      scheme.startsWith("scheme_1") && (
                        <th key={index}>{t('profile.enabledFor')} {basicInfo.schemes[scheme].toUpperCase()}</th>
                      )
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(basicInfo.time_based_adjustments).map(([adjustmentId, adjustmentDetails]) => (
                    <tr key={adjustmentId}>
                      <td>{translateBackendValue(adjustmentDetails.name)}</td>
                      {Object.keys(basicInfo.schemes).map((scheme) => (
                        scheme.startsWith("scheme_1") && (
                          <td key={`${adjustmentId}-${scheme}`}>
                            <input
                              type="checkbox"
                              checked={!!profile.time_based_adjustments?.[adjustmentId]?.[scheme]}
                              onChange={(e) =>
                                onTimeBasedAdjustmentToggle(
                                  profile.id,
                                  `time_based_adjustments.${adjustmentId}.${scheme}`,
                                  e.target.checked
                                )
                              }
                            />
                          </td>
                        )
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {profile.reserve_heating && profile.reserve_heating.hours !== undefined && profile.reserve_heating.reserve_degrees !== undefined ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>{t('profile.reserveHeating')}</th>
                    <th>{t('profile.hours')}</th>
                    <th>{t('profile.degrees')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t('profile.reserveHeatingDesc')} <i>{t('profile.hours')}</i> {t('profile.reserveHeatingDesc2')} <i>{t('profile.degrees')}</i> </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating.hours}
                        onChange={(value) => onReserveChange(profile.id, 'hours', value)}
                        min={0}
                        max={24}
                        step={1}
                      />
                    </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating.reserve_degrees}
                        onChange={(value) => onReserveChange(profile.id, 'reserve_degrees', value)}
                        min={0}
                        max={10}
                        step={0.5}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : null}

            {profile.reserve_heating_afternoon && (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>{t('profile.reserveHeatingAfternoon')}</th>
                    <th>{t('profile.hours')}</th>
                    <th>{t('profile.degrees')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t('profile.afternoonDesc')} <i>{t('profile.hours')}</i> {t('profile.reserveHeatingDesc2')} <i>{t('profile.degrees')}</i> </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating_afternoon.hours}
                        onChange={(value) => onReserveHeatingAfternoonChange(profile.id, 'reserve_heating_afternoon', 'hours', value)}
                        min={0}
                        max={24}
                        step={1}
                      />
                    </td>
                    <td>
                      <NumericInput
                        value={profile.reserve_heating_afternoon.reserve_degrees}
                        onChange={(value) => onReserveHeatingAfternoonChange(profile.id, 'reserve_heating_afternoon', 'reserve_degrees', value)}
                        min={0}
                        max={10}
                        step={0.5}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            )}

            {profile.price_based_drops && profile.price_based_drops.reference_price_name !== undefined ? (
              <table className="profile-table">
                <thead>
                  <tr>
                    <th>{t('profile.tempAdjustments')}</th>
                    {Object.keys(profile.price_based_drops)
                      .filter(key => key !== 'reference_price_name')
                      .map(scheme => (
                        <th key={scheme}>{t('profile.tempChange')} {scheme.toUpperCase()} {t('profile.degreesUnit')}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t('profile.priceChangeDesc')} <i>{profile.price_based_drops.reference_price_name}</i> {t('profile.by')} <i>{t('profile.degreesUnit')}</i></td>
                    {Object.entries(profile.price_based_drops)
                      .filter(([scheme]) => scheme !== 'reference_price_name')
                      .map(([scheme, dropValue]) => (
                        <td key={scheme}>
                          <NumericInput
                            value={dropValue}
                            onChange={(value) => onPriceDropChange(profile.id, scheme, value)}
                            min={-5}
                            max={5}
                            step={0.5}
                          />
                        </td>
                      ))}
                  </tr>
                </tbody>
              </table>
            ) : null}

            {/* Thermostat Settings */}
            {profile.thermostat_settings && (
              <div>
                <table className="profile-table">
                  <thead>
                    <tr>
                      <th>{t('profile.thermostatSettings')}</th>
                      <th>{t('profile.parameter')}</th>
                      <th>{t('profile.value')}</th>
                    </tr>
                  </thead>
                  <tbody>

                    
                  {Object.entries(profile.thermostat_settings).map(([thermostatId, settings]) => (
                  <React.Fragment key={thermostatId}>
                    {/* Render the thermostat name */}
                    <tr>
                      <td colSpan="3"><b>{settings.name}</b></td>
                    </tr>
                    
                    {/* Render the settings */}
                    {Object.entries(settings)
                      .filter(([key]) => key !== 'name') // Exclude the 'name' property
                      .map(([key, setting]) => {
                        
                        // Handle reserve_heating
                        if (key === 'reserve_heating') {
                          return (
                            <React.Fragment key={key}>
                              <tr>
                                <td></td>
                                <td>{t('thermostat.reserveHeating')}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={setting.enabled}
                                    onChange={(e) =>
                                      onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, enabled: e.target.checked })
                                    }
                                  />
                                </td>
                              </tr>
                              {setting.enabled && (
                                <>
                                  <tr>
                                    <td></td>
                                    <td>{t('profile.hours')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.hours}
                                        onChange={(value) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, hours: value })}
                                        min={0} max={24} step={1}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td>{t('profile.degrees')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.reserve_degrees}
                                        onChange={(value) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, reserve_degrees: value })}
                                        min={0} max={5} step={0.5}
                                      />
                                    </td>
                                  </tr>
                                </>
                              )}
                            </React.Fragment>
                          );
                        }

                        // Handle comfort_heating
                        if (key === 'comfort_heating') {
                          return (
                            <React.Fragment key={key}>
                              <tr>
                                <td></td>
                                <td>{t('thermostat.comfortHeating')}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={setting.enabled}
                                    onChange={(e) =>
                                      onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, enabled: e.target.checked })
                                    }
                                  />
                                </td>
                              </tr>
                              {setting.enabled && (
                                <>
                                  <tr>
                                    <td></td>
                                    <td>{t('thermostat.startHour')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.start_hour}
                                        onChange={(value) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, start_hour: value })}
                                        min={0} max={23} step={1}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td>{t('thermostat.endHour')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.end_hour}
                                        onChange={(value) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, end_hour: value })}
                                        min={0} max={23} step={1}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td>{t('thermostat.priceCriteriaType')}</td>
                                    <td>
                                      <select
                                        value={setting.price_criteria_type || 'ratio'}
                                        onChange={(e) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, price_criteria_type: e.target.value })}
                                      >
                                        <option value="ratio">{t('thermostat.priceCriteriaRatio')}</option>
                                        <option value="absolute">{t('thermostat.priceCriteriaAbsolute')}</option>
                                      </select>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td>{(setting.price_criteria_type || 'ratio') === 'ratio' ? t('thermostat.priceCriteriaRatioLabel') : t('thermostat.priceCriteriaAbsoluteLabel')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.price_criteria}
                                        onChange={(value) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, price_criteria: value })}
                                        min={0}
                                        max={(setting.price_criteria_type || 'ratio') === 'ratio' ? 2 : 50}
                                        step={(setting.price_criteria_type || 'ratio') === 'ratio' ? 0.05 : 0.5}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td>{t('profile.degrees')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.degrees}
                                        onChange={(value) => onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, degrees: value })}
                                        min={0} max={5} step={0.5}
                                      />
                                    </td>
                                  </tr>
                                </>
                              )}
                            </React.Fragment>
                          );
                        }

                        // Handle pv_controls (heating_temp / cooling_temp)
                        if (key === 'pv_controls' && typeof setting === 'object') {
                          return (
                            <React.Fragment key={key}>
                              <tr>
                                <td></td>
                                <td colSpan="2"><b>{t('profile.pvSettings')}</b></td>
                              </tr>
                              <tr>
                                <td></td>
                                <td>{t('common.enabled')}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={!!setting.enabled}
                                    onChange={(e) =>
                                      onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, enabled: e.target.checked })
                                    }
                                  />
                                </td>
                              </tr>
                              {setting.enabled && (
                                <>
                                  <tr>
                                    <td></td>
                                    <td>{t('profile.pvHeatingTemp')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.heating_temp ?? 23}
                                        onChange={(value) =>
                                          onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, heating_temp: parseFloat(value) || 23 })
                                        }
                                        min={15} max={30} step={0.5}
                                      />
                                    </td>
                                  </tr>
                                  <tr>
                                    <td></td>
                                    <td>{t('profile.pvCoolingTemp')}</td>
                                    <td>
                                      <NumericInput
                                        value={setting.cooling_temp ?? 24}
                                        onChange={(value) =>
                                          onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, cooling_temp: parseFloat(value) || 24 })
                                        }
                                        min={15} max={30} step={0.5}
                                      />
                                    </td>
                                  </tr>
                                </>
                              )}
                            </React.Fragment>
                          );
                        }

                        // Handle cooling_mode (AHP only)
                        if (key === 'cooling_mode' && typeof setting === 'object') {
                          const coolingActive = !!setting.active;
                          return (
                            <React.Fragment key={key}>
                              <tr>
                                <td></td>
                                <td><b>{t('thermostat.coolingMode')}</b></td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={coolingActive}
                                    disabled={!summerActive}
                                    title={!summerActive ? t('thermostat.coolingRequiresSummer') : ''}
                                    onChange={(e) =>
                                      onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, active: e.target.checked })
                                    }
                                  />
                                </td>
                              </tr>
                              {!summerActive && (
                                <tr>
                                  <td></td>
                                  <td colSpan="2" style={{ fontStyle: 'italic', color: '#9ca3af', fontSize: '0.85rem' }}>
                                    {t('thermostat.coolingRequiresSummer')}
                                  </td>
                                </tr>
                              )}
                              {coolingActive && summerActive && (
                                <tr>
                                  <td></td>
                                  <td>{t('thermostat.coolingTemp')}</td>
                                  <td>
                                    <NumericInput
                                      value={setting.cooling_temp ?? 22}
                                      onChange={(value) =>
                                        onThermostatSettingChange(profile.id, thermostatId, key, { ...setting, cooling_temp: parseFloat(value) || 22 })
                                      }
                                      min={16} max={28} step={0.5}
                                    />
                                  </td>
                                </tr>
                              )}
                              {coolingActive && summerActive && (
                                <tr>
                                  <td></td>
                                  <td colSpan="2" style={{ fontStyle: 'italic', color: '#0369a1', fontSize: '0.85rem' }}>
                                    {t('thermostat.coolingModeActive', { temp: setting.cooling_temp ?? 22 })}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        }

                        // Handle exclude_summer_mode
                        if (key === 'exclude_summer_mode') {
                          return (
                            <tr key={key}>
                              <td></td>
                              <td>{t('thermostat.excludeSummerMode')}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={!!setting}
                                  onChange={(e) =>
                                    onThermostatSettingChange(profile.id, thermostatId, key, e.target.checked)
                                  }
                                />
                              </td>
                            </tr>
                          );
                        }
                        
                        // Handle flat boolean settings
                        if (typeof setting === 'boolean') {
                          return (
                            <tr key={key}>
                              <td></td>
                              <td>{getSettingLabel(key)}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={setting}
                                  onChange={(e) =>
                                    onThermostatSettingChange(
                                      profile.id,
                                      thermostatId,
                                      key,
                                      e.target.checked
                                    )
                                  }
                                />
                              </td>
                            </tr>
                          );
                        }
                        
                        // Handle flat numeric settings
                        if (typeof setting === 'number') {
                          return (
                            <tr key={key}>
                              <td></td>
                              <td>{getSettingLabel(key)}</td>
                              <td>
                                <NumericInput
                                  value={setting}
                                  onChange={(value) =>
                                    onThermostatSettingChange(
                                      profile.id,
                                      thermostatId,
                                      key,
                                      parseFloat(value) || 0
                                    )
                                  }
                                  min={getSettingMin(key)}
                                  max={getSettingMax(key)}
                                  step={getSettingStep(key)}
                                />
                              </td>
                            </tr>
                          );
                        }
                        
                        // Ignore other types (strings, objects we don't recognize, etc.)
                        return null;
                      })}
                  </React.Fragment>
                ))}


                  </tbody>
                </table>
              </div>
            )}

          </div>
        ))}
      </div>
    </div>
  );
}